import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  QrCode,
  Search,
  MapPin,
  User,
  Loader2,
  CheckCircle2,
  Copy,
  Check,
  Send,
  Clock
} from "lucide-react";
import { cn } from "@/lib/utils";
import { QRCodeSVG } from "qrcode.react";

export default function CheckIn() {
  const { token, user } = useAuth();
  const [members, setMembers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState("");
  const [memberSearchOpen, setMemberSearchOpen] = useState(false);
  const [memberSearch, setMemberSearch] = useState("");
  const [checkInResult, setCheckInResult] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [membersRes, locationsRes] = await Promise.all([
          axios.get(`${API}/members`, {
            headers: { Authorization: `Bearer ${token}` },
            params: { status: "active" }
          }),
          axios.get(`${API}/locations`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);
        setMembers(membersRes.data);
        setLocations(locationsRes.data);
        
        // Auto-select location if user only has one
        if (locationsRes.data.length === 1) {
          setSelectedLocation(locationsRes.data[0].id);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Failed to load data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token]);

  const filteredMembers = members.filter((member) => {
    const searchLower = memberSearch.toLowerCase();
    return (
      member.name.toLowerCase().includes(searchLower) ||
      member.member_id.toLowerCase().includes(searchLower)
    );
  });

  const handleCheckIn = async () => {
    if (!selectedMember || !selectedLocation) {
      toast.error("Please select a member and location");
      return;
    }

    setChecking(true);
    try {
      const response = await axios.post(
        `${API}/check-in`,
        {
          member_id: selectedMember.id,
          location_id: selectedLocation
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setCheckInResult(response.data);
      
      // Show appropriate message based on whether already checked in
      if (response.data.already_checked_in) {
        toast.info("Member has already checked in today. Showing existing code.");
      } else {
        toast.success("Check-in successful!");
      }
    } catch (error) {
      console.error("Check-in error:", error);
      toast.error(error.response?.data?.detail || "Check-in failed");
    } finally {
      setChecking(false);
    }
  };

  const handleCopyCode = async () => {
    if (!checkInResult?.code) return;
    try {
      await navigator.clipboard.writeText(checkInResult.code);
      setCopied(true);
      toast.success("Code copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error("Failed to copy code");
    }
  };

  const handleNewCheckIn = () => {
    setCheckInResult(null);
    setSelectedMember(null);
    setMemberSearch("");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto" data-testid="checkin-page">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-primary rounded-2xl mb-4">
          <QrCode className="w-8 h-8 text-white" />
        </div>
        <h1 className="text-3xl font-bold text-gray-900">Member Check-In</h1>
        <p className="text-gray-500 mt-2">
          Generate a unique 12-digit code for member check-in
        </p>
      </div>

      {!checkInResult ? (
        <Card className="border-0 shadow-lg" data-testid="checkin-form">
          <CardContent className="p-8 space-y-6">
            {/* Member Selection */}
            <div className="space-y-2">
              <Label className="text-base">Select Member</Label>
              <Popover open={memberSearchOpen} onOpenChange={setMemberSearchOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={memberSearchOpen}
                    className="w-full justify-between h-12 text-left"
                    data-testid="member-select-btn"
                  >
                    {selectedMember ? (
                      <span className="flex items-center gap-2">
                        <User className="h-4 w-4 text-gray-400" />
                        {selectedMember.name}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">Search for a member...</span>
                    )}
                    <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0" align="start">
                  <Command>
                    <CommandInput 
                      placeholder="Search by name or member ID..." 
                      value={memberSearch}
                      onValueChange={setMemberSearch}
                      data-testid="member-search-input"
                    />
                    <CommandList>
                      <CommandEmpty>No members found.</CommandEmpty>
                      <CommandGroup>
                        {filteredMembers.slice(0, 10).map((member) => (
                          <CommandItem
                            key={member.id}
                            value={`${member.name} ${member.member_id}`}
                            onSelect={() => {
                              setSelectedMember(member);
                              setMemberSearchOpen(false);
                            }}
                            data-testid={`member-option-${member.id}`}
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                                <User className="h-4 w-4 text-gray-500" />
                              </div>
                              <div>
                                <p className="font-medium">
                                  {member.name}
                                </p>
                                <p className="text-sm text-gray-500">ID: {member.member_id}</p>
                              </div>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {/* Location Selection */}
            <div className="space-y-2">
              <Label className="text-base">Select Location</Label>
              <Select value={selectedLocation} onValueChange={setSelectedLocation}>
                <SelectTrigger className="h-12" data-testid="location-select">
                  <SelectValue placeholder="Choose a location..." />
                </SelectTrigger>
                <SelectContent>
                  {locations.map((location) => (
                    <SelectItem key={location.id} value={location.id}>
                      <span className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-gray-400" />
                        {location.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Check-in Button */}
            <Button
              onClick={handleCheckIn}
              disabled={checking || !selectedMember || !selectedLocation}
              className="w-full h-12 text-lg btn-press"
              data-testid="checkin-btn"
            >
              {checking ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Checking In...
                </>
              ) : (
                <>
                  <QrCode className="mr-2 h-5 w-5" />
                  Generate Check-In Code
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-0 shadow-lg animate-slide-up" data-testid="checkin-result">
          <CardContent className="p-8 text-center space-y-6">
            {/* Show different icon/message based on whether already checked in */}
            {checkInResult.already_checked_in ? (
              <>
                <div className="inline-flex items-center justify-center w-16 h-16 bg-yellow-100 rounded-full mb-2">
                  <Clock className="w-8 h-8 text-yellow-600" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-1">Already Checked In Today</h2>
                  <p className="text-gray-500">
                    {checkInResult.member_name} at {checkInResult.location_name}
                  </p>
                  <p className="text-sm text-yellow-600 mt-2">
                    This member has already checked in within the last 24 hours.
                  </p>
                </div>
              </>
            ) : (
              <>
                <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-2">
                  <CheckCircle2 className="w-8 h-8 text-green-600" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-1">Check-In Successful</h2>
                  <p className="text-gray-500">
                    {checkInResult.member_name} at {checkInResult.location_name}
                  </p>
                </div>
              </>
            )}

            {/* Code Display */}
            <div className="py-6">
              <p className="text-sm text-gray-500 mb-3">
                {checkInResult.already_checked_in ? "Existing check-in code" : "Your 12-digit check-in code"}
              </p>
              
              {/* QR Code */}
              <div className="flex justify-center mb-4">
                <div className="bg-white p-3 rounded-lg shadow-sm border" data-testid="checkin-qrcode">
                  <QRCodeSVG 
                    value={checkInResult.code} 
                    size={150}
                    level="H"
                    includeMargin={false}
                  />
                </div>
              </div>
              
              <div className="check-in-code inline-flex items-center gap-4" data-testid="checkin-code">
                {checkInResult.code}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleCopyCode}
                  data-testid="copy-code-btn"
                >
                  {copied ? (
                    <Check className="h-5 w-5 text-green-600" />
                  ) : (
                    <Copy className="h-5 w-5" />
                  )}
                </Button>
              </div>
            </div>

            {/* Webhook Status - only show for new check-ins */}
            {!checkInResult.already_checked_in && (
              <div className="flex items-center justify-center gap-2 text-sm">
                <span className="flex items-center gap-2 text-green-600">
                  <Send className="h-4 w-4" />
                  Data sent successfully
                </span>
              </div>
            )}

            <Button
              onClick={handleNewCheckIn}
              variant="outline"
              className="mt-4"
              data-testid="new-checkin-btn"
            >
              New Check-In
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Quick Stats */}
      {!checkInResult && members.length === 0 && (
        <Card className="border-0 shadow-sm mt-6">
          <CardContent className="p-6 text-center">
            <User className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p className="text-gray-500">No active members found</p>
            <p className="text-sm text-gray-400 mt-1">
              Add members to start checking them in
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
